import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  MediBlogArticle,
  MediBlogCategory,
  MediBlogArticleStatus,
  slugify,
} from "../../../../services/mediBlog";
import ImageUpload from "../../../../components/ImageUpload";

interface MediBlogArticleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editingArticle: MediBlogArticle | null;
  setEditingArticle: React.Dispatch<React.SetStateAction<MediBlogArticle | null>>;
  categories: MediBlogCategory[];
}

const STATUS_OPTIONS: MediBlogArticleStatus[] = ["draft", "published", "archived"];

const MediBlogArticleDialog: React.FC<MediBlogArticleDialogProps> = ({
  open,
  onClose,
  onSave,
  editingArticle,
  setEditingArticle,
  categories,
}) => {
  const { t } = useTranslation();

  const handleChange = (field: keyof MediBlogArticle, value: unknown) => {
    if (!editingArticle) return;
    const next = { ...editingArticle, [field]: value };
    if (field === "title_en" && typeof value === "string" && !editingArticle.id) {
      next.slug = slugify(value);
    }
    setEditingArticle(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {editingArticle?.id
          ? t("admin.contentManagement.mediblog.editArticle")
          : t("admin.contentManagement.mediblog.addArticle")}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="subtitle2" color="primary">
            {t("admin.contentManagement.mediblog.sectionContent")}
          </Typography>

          <TextField
            label={t("admin.contentManagement.fields.titleEn")}
            fullWidth
            required
            value={editingArticle?.title_en || ""}
            onChange={(e) => handleChange("title_en", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleAr")}
            fullWidth
            value={editingArticle?.title_ar || ""}
            onChange={(e) => handleChange("title_ar", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.fields.titleFr")}
            fullWidth
            value={editingArticle?.title_fr || ""}
            onChange={(e) => handleChange("title_fr", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.mediblog.slug")}
            fullWidth
            required
            value={editingArticle?.slug || ""}
            onChange={(e) => handleChange("slug", slugify(e.target.value))}
            helperText={t("admin.contentManagement.mediblog.articleSlugHelper")}
          />
          <TextField
            select
            label={t("admin.contentManagement.mediblog.category")}
            fullWidth
            value={editingArticle?.category_id || ""}
            onChange={(e) => handleChange("category_id", e.target.value || null)}
          >
            <MenuItem value="">{t("admin.contentManagement.mediblog.noCategory")}</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name_en}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t("admin.contentManagement.mediblog.author")}
            fullWidth
            value={editingArticle?.author_name || ""}
            onChange={(e) => handleChange("author_name", e.target.value)}
          />
          <ImageUpload
            currentImage={editingArticle?.featured_image || ""}
            onImageChange={(url) => handleChange("featured_image", url)}
            folder="mediblog"
            label={t("admin.contentManagement.mediblog.featuredImage")}
          />

          <TextField
            label={t("admin.contentManagement.mediblog.excerptEn")}
            fullWidth
            multiline
            rows={2}
            value={editingArticle?.excerpt_en || ""}
            onChange={(e) => handleChange("excerpt_en", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.mediblog.contentEn")}
            fullWidth
            required
            multiline
            rows={6}
            value={editingArticle?.content_en || ""}
            onChange={(e) => handleChange("content_en", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.mediblog.contentAr")}
            fullWidth
            multiline
            rows={4}
            value={editingArticle?.content_ar || ""}
            onChange={(e) => handleChange("content_ar", e.target.value)}
          />
          <TextField
            label={t("admin.contentManagement.mediblog.contentFr")}
            fullWidth
            multiline
            rows={4}
            value={editingArticle?.content_fr || ""}
            onChange={(e) => handleChange("content_fr", e.target.value)}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField
              select
              label={t("admin.contentManagement.mediblog.statusLabel")}
              fullWidth
              value={editingArticle?.status || "draft"}
              onChange={(e) => handleChange("status", e.target.value as MediBlogArticleStatus)}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {t(`admin.contentManagement.mediblog.status.${s}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t("admin.contentManagement.fields.displayOrder")}
              fullWidth
              type="number"
              value={editingArticle?.display_order ?? 0}
              onChange={(e) => handleChange("display_order", parseInt(e.target.value, 10) || 0)}
            />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography fontWeight={600}>{t("admin.contentManagement.mediblog.seoSection")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label={t("admin.contentManagement.mediblog.metaTitle")}
                  fullWidth
                  value={editingArticle?.meta_title || ""}
                  onChange={(e) => handleChange("meta_title", e.target.value)}
                  helperText={t("admin.contentManagement.mediblog.metaTitleHelper")}
                  inputProps={{ maxLength: 70 }}
                />
                <TextField
                  label={t("admin.contentManagement.mediblog.metaDescription")}
                  fullWidth
                  multiline
                  rows={2}
                  value={editingArticle?.meta_description || ""}
                  onChange={(e) => handleChange("meta_description", e.target.value)}
                  helperText={t("admin.contentManagement.mediblog.metaDescriptionHelper")}
                  inputProps={{ maxLength: 160 }}
                />
                <TextField
                  label={t("admin.contentManagement.mediblog.metaKeywords")}
                  fullWidth
                  value={editingArticle?.meta_keywords || ""}
                  onChange={(e) => handleChange("meta_keywords", e.target.value)}
                  helperText={t("admin.contentManagement.mediblog.metaKeywordsHelper")}
                />
                <TextField
                  label={t("admin.contentManagement.mediblog.ogTitle")}
                  fullWidth
                  value={editingArticle?.og_title || ""}
                  onChange={(e) => handleChange("og_title", e.target.value)}
                />
                <TextField
                  label={t("admin.contentManagement.mediblog.ogDescription")}
                  fullWidth
                  multiline
                  rows={2}
                  value={editingArticle?.og_description || ""}
                  onChange={(e) => handleChange("og_description", e.target.value)}
                />
                <ImageUpload
                  currentImage={editingArticle?.og_image || ""}
                  onImageChange={(url) => handleChange("og_image", url)}
                  folder="mediblog/og"
                  label={t("admin.contentManagement.mediblog.ogImage")}
                />
                <TextField
                  label={t("admin.contentManagement.mediblog.canonicalUrl")}
                  fullWidth
                  value={editingArticle?.canonical_url || ""}
                  onChange={(e) => handleChange("canonical_url", e.target.value)}
                  placeholder="https://zettamed.com/mediblog/article-slug"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingArticle?.no_index ?? false}
                      onChange={(e) => handleChange("no_index", e.target.checked)}
                    />
                  }
                  label={t("admin.contentManagement.mediblog.noIndex")}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("admin.contentManagement.cancel")}</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!editingArticle?.title_en || !editingArticle?.slug || !editingArticle?.content_en}
        >
          {t("admin.contentManagement.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediBlogArticleDialog;
